// ============================================================================
// src/app/externalSources/weatherForecastSource.ts
// ----------------------------------------------------------------------------
// The explicit owner of the public weather-forecast HTTP source.
//
// This is intentionally outside `app/data/`: it is neither a Supabase query
// nor a route resource, has no user scope, and contributes no boot-release
// eligibility. HeaderWeatherChip and WeatherPage subscribe to this same source
// so one component unmounting cannot cancel the other component's request.
// ============================================================================

import {useSyncExternalStore} from 'react';

import {WEATHER_FORECAST_CACHE_MS, fetchWeatherForecast} from '../../lib/weather.js';
import type {WeatherForecast} from '../../lib/weather.js';
import {createExternalSourceRegistry} from './externalSourceRegistry.js';
import type {ExternalSourceSnapshot} from './externalSourceRegistry.js';

export const weatherForecastSource = createExternalSourceRegistry<WeatherForecast>({
  cacheMs: WEATHER_FORECAST_CACHE_MS,
  load: fetchWeatherForecast,
});

/** React subscription seam for every weather consumer. */
export function useWeatherForecastSource(): ExternalSourceSnapshot<WeatherForecast> {
  return useSyncExternalStore(
    weatherForecastSource.subscribe,
    weatherForecastSource.getSnapshot,
    weatherForecastSource.getSnapshot,
  );
}

/** Explicit user refresh; concurrent refresh callers join the same request. */
export function refreshWeatherForecast(): Promise<WeatherForecast | null> {
  return weatherForecastSource.load({force: true});
}
